import { cn } from '@/shared/lib/cn';

/**
 * Chave liga/desliga padrão do sistema (mesmo desenho da do filtro do
 * Atendimento). Pra opção de liga/desliga use isto; caixa de marcar fica pra
 * escolher itens de uma lista.
 */
export function Switch({
  checked,
  onChange,
  disabled = false,
  label,
  size = 'sm',
}: {
  checked: boolean;
  onChange: (valor: boolean) => void;
  disabled?: boolean;
  /** Nome acessível (leitor de tela), quando não há texto visível colado. */
  label?: string;
  /** sm = compacta (filtros, menus); md = cards de configuração. */
  size?: 'sm' | 'md';
}) {
  const md = size === 'md';
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative inline-flex flex-shrink-0 items-center rounded-pill transition-colors disabled:cursor-not-allowed disabled:opacity-50',
        md ? 'h-[22px] w-[40px]' : 'h-[18px] w-[32px]',
        checked ? 'bg-brand-500' : 'bg-n-300',
      )}
    >
      <span
        className={cn(
          'pointer-events-none absolute rounded-pill bg-n-0 shadow-sm transition-transform',
          md ? 'h-[18px] w-[18px]' : 'h-[14px] w-[14px]',
          checked ? (md ? 'translate-x-[20px]' : 'translate-x-[16px]') : 'translate-x-[2px]',
        )}
      />
    </button>
  );
}

/**
 * Opção liga/desliga em card: chave à esquerda, colada no título (e explicação
 * curta) — padrão escolhido pelo dono em 27/out/2026 (Editar usuário, Motivos
 * de fechamento). O card inteiro é clicável. `switchSide="right"` põe a chave
 * no fim do card, como o "Abrir atendimento ao enviar" do agendamento.
 * `labelSize="sm"` deixa o título no tamanho dos rótulos de campo (12px), pra
 * card no meio de um formulário.
 */
export function ToggleCard({
  label,
  hint,
  checked,
  onChange,
  disabled = false,
  switchSide = 'left',
  labelSize = 'md',
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (valor: boolean) => void;
  disabled?: boolean;
  switchSide?: 'left' | 'right';
  labelSize?: 'sm' | 'md';
}) {
  const chave = (
    <span
      className={cn('flex', hint && 'mt-0.5')}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <Switch checked={checked} onChange={onChange} disabled={disabled} label={label} size="md" />
    </span>
  );
  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: o teclado usa a própria chave (button role=switch)
    <div
      onClick={() => {
        if (!disabled) onChange(!checked);
      }}
      className={cn(
        // Sem borda nem fundo (teste do dono, 27/out/2026): sem padding lateral
        // pra chave alinhar com a borda dos campos do formulário.
        // Com explicação, título e chave alinham pelo topo; sem, centralizam.
        'flex gap-3 py-1.5 text-left',
        switchSide === 'right' && 'justify-between',
        hint ? 'items-start' : 'items-center',
        disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
      )}
    >
      {switchSide === 'left' && chave}
      <span className="flex min-w-0 flex-col gap-0.5">
        <span
          className={cn(
            'font-semibold text-n-800 leading-snug',
            labelSize === 'sm' ? 'text-[12px]' : 'text-[13px]',
          )}
        >
          {label}
        </span>
        {hint && <span className="text-[11px] text-n-500 leading-snug">{hint}</span>}
      </span>
      {switchSide === 'right' && chave}
    </div>
  );
}
